import React, { useState, useEffect, useRef } from 'react';
import {
  CheckCircle2,
  XCircle,
  ArrowRight,
  Copy,
  Briefcase,
  Sparkles,
  Loader2,
  TrendingUp,
  AlertCircle,
  LogOut,
  User,
  ChevronDown,
  ChevronUp,
  Award,
  BookOpen,
  Check,
  ChevronRight
} from 'lucide-react';
import { supabase } from './supabaseClient';
import { useNavigate } from 'react-router-dom';

const handleApiResponse = async (res, defaultError = 'Request failed') => {
  const contentType = res.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    const json = await res.json();
    if (!res.ok) {
      throw new Error(json.error || defaultError);
    }
    return json;
  }
  const text = await res.text();
  let errorMsg = defaultError;
  if (res.status === 404) {
    errorMsg = `API endpoint not found (404). If running locally, please start the project using 'vercel dev' instead of 'npm start' to enable backend API routes.`;
  } else if (text.trim().startsWith('<')) {
    errorMsg = `Invalid server response (${res.status}). If running locally, make sure to run the project using 'vercel dev' so that the serverless functions in the /api directory are served.`;
  } else {
    errorMsg = `Server error (${res.status}): ${text.substring(0, 100) || 'No details available'}`;
  }
  throw new Error(errorMsg);
};

const plans = [
  { id: 'starter', name: 'Starter', price: 9, credits: 20, stripeUrl: 'https://buy.stripe.com/00w9AL3TgcBn04h83i8g004' },
  { id: 'pro', name: 'Pro', price: 19, credits: 100, stripeUrl: 'https://buy.stripe.com/cNifZ90H48l718l1EU8g003', recommended: true }
];

const PaywallModal = ({ onClose, onPaymentSuccess, onServerVerifyPaypal, initialPlanId, userId, userEmail }) => {
  const [method, setMethod] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    if (initialPlanId) {
      const plan = plans.find(p => p.id === initialPlanId);
      if (plan) setSelectedPlan(plan);
    }
  }, [initialPlanId]);

  useEffect(() => {
    if (method === 'paypal' && selectedPlan) {
      const container = document.getElementById('paypal-button-container');
      if (container) container.innerHTML = '';

      window.paypal.Buttons({
        createOrder: (data, actions) => {
          return actions.order.create({
            purchase_units: [{
              amount: { value: selectedPlan.price.toString() },
              description: `${selectedPlan.credits} CV Matcher Credits`
            }]
          });
        },
        onApprove: async (data, actions) => {
          await actions.order.capture();
          onServerVerifyPaypal(data.orderID);
        },
        style: {
          layout: 'vertical',
          color: 'blue',
          shape: 'rect',
          label: 'paypal'
        }
      }).render('#paypal-button-container');
    }
  }, [method, selectedPlan, onPaymentSuccess, onServerVerifyPaypal]);

  return (
    <div className="flex flex-col items-center p-6 md:p-8 text-center bg-gray-900/95 backdrop-blur-xl border border-indigo-500/30 rounded-3xl shadow-2xl max-w-2xl w-full mx-auto relative max-h-[90vh] overflow-y-auto">
      {onClose && (
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors">
          <XCircle className="w-8 h-8" />
        </button>
      )}
      
      <div className="w-16 h-16 rounded-2xl bg-indigo-500/20 flex items-center justify-center mb-6 mt-4">
        <Sparkles className="w-8 h-8 text-indigo-400" />
      </div>
      
      <h3 className="text-3xl font-bold mb-2 text-white">Unlock Mock Interviews & Coaching</h3>
      <p className="text-indigo-200/70 mb-8 text-lg">
        Choose a plan to continue practicing and refining your skills.
      </p>

      {!selectedPlan ? (
        <div className="grid sm:grid-cols-2 gap-6 w-full mb-6">
          {plans.map((plan) => (
            <div 
              key={plan.id}
              onClick={() => setSelectedPlan(plan)}
              className={`p-6 rounded-2xl border transition-all cursor-pointer text-left flex flex-col relative ${
                plan.recommended 
                  ? 'bg-indigo-600/10 border-indigo-500 shadow-[0_0_20px_-5px_rgba(99,102,241,0.3)]' 
                  : 'bg-gray-950 border-gray-800 hover:border-gray-700'
              }`}
            >
              {plan.recommended && (
                <div className="absolute -top-3 left-6 bg-indigo-500 text-white text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-wider">
                  Best Value
                </div>
              )}
              <h4 className="text-xl font-bold text-white mb-1">{plan.name}</h4>
              <div className="mb-4">
                <span className="text-3xl font-bold text-white">${plan.price}</span>
                <span className="text-gray-400 text-sm"> / {plan.credits} credits</span>
              </div>
              <ul className="space-y-3 mb-6 text-sm text-gray-400">
                <li className="flex items-center"><CheckCircle2 className="w-4 h-4 text-indigo-400 mr-2 shrink-0" /> {plan.credits} interview sessions</li>
                <li className="flex items-center"><CheckCircle2 className="w-4 h-4 text-indigo-400 mr-2 shrink-0" /> Tailored AI coaching</li>
                <li className="flex items-center"><CheckCircle2 className="w-4 h-4 text-indigo-400 mr-2 shrink-0" /> Perfect answers suggestions</li>
              </ul>
              <button className={`w-full py-3 rounded-xl font-bold transition-all ${
                plan.recommended ? 'bg-indigo-600 text-white' : 'bg-gray-800 text-gray-300'
              }`}>
                Get {plan.credits} sessions — ${plan.price}
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="w-full animate-in fade-in slide-in-from-bottom-4 duration-300">
          <button 
            onClick={() => { setSelectedPlan(null); setMethod(null); }}
            className="mb-6 text-sm text-gray-400 hover:text-white flex items-center transition-colors"
          >
            ← Back to plans
          </button>
          
          <div className="bg-gray-950/50 border border-gray-800 rounded-2xl p-6 mb-8 text-left">
            <div className="flex justify-between items-center">
              <div>
                <h4 className="font-bold text-white text-lg">{selectedPlan.name} Plan</h4>
                <p className="text-gray-400 text-sm">{selectedPlan.credits} credits for your account</p>
              </div>
              <div className="text-2xl font-bold text-white">${selectedPlan.price}</div>
            </div>
          </div>

          <div className="grid gap-4">
            <a 
              href={`${selectedPlan.stripeUrl}?client_reference_id=${encodeURIComponent(userId || '')}&prefilled_email=${encodeURIComponent(userEmail || '')}`}
              className="w-full py-4 bg-[#635BFF] hover:bg-[#5851E0] text-white font-bold rounded-xl transition-all flex items-center justify-center shadow-lg"
            >
              <span>Pay with Card / Apple Pay</span>
            </a>

            <div className="relative my-2">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-800"></div></div>
              <div className="relative flex justify-center text-xs uppercase"><span className="bg-[#0B0F19] px-2 text-gray-500">Or</span></div>
            </div>

            <div className="w-full" onClick={() => setMethod('paypal')}>
              {!method && (
                <button className="w-full py-4 bg-[#FFC439] hover:bg-[#F2BA36] text-[#003087] font-bold rounded-xl transition-all flex items-center justify-center shadow-lg">
                  Pay with PayPal
                </button>
              )}
              <div id="paypal-button-container" className="w-full min-h-[50px]"></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StepIndicator = ({ currentStep }) => {
  const steps = [
    { number: 1, title: 'Job Info', description: 'Paste details' },
    { number: 2, title: 'Interview Type', description: 'Choose focus' },
    { number: 3, title: 'Q&A Practice', description: 'Answer questions' },
    { number: 4, title: 'Evaluation', description: 'AI feedback' }
  ];

  return (
    <div className="max-w-3xl mx-auto mb-10 px-4">
      <div className="flex items-center justify-between relative">
        <div className="absolute top-5 left-0 w-full h-0.5 bg-gray-800 -z-10" />
        <div 
          className="absolute top-5 left-0 h-0.5 bg-indigo-500 transition-all duration-500 -z-10" 
          style={{ width: `${((currentStep - 1) / (steps.length - 1)) * 100}%` }}
        />
        
        {steps.map((step) => (
          <div key={step.number} className="flex flex-col items-center">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
              currentStep >= step.number 
                ? 'bg-indigo-600 border-indigo-500 text-white shadow-[0_0_15px_rgba(79,70,229,0.4)]' 
                : 'bg-gray-900 border-gray-800 text-gray-500'
            }`}>
              {currentStep > step.number ? <CheckCircle2 className="w-6 h-6" /> : <span className="font-bold">{step.number}</span>}
            </div>
            <div className="mt-3 text-center">
              <div className={`text-xs sm:text-sm font-bold ${currentStep >= step.number ? 'text-white' : 'text-gray-500'}`}>{step.title}</div>
              <div className="text-[10px] text-gray-500 hidden sm:block uppercase tracking-wider mt-0.5">{step.description}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const InterviewCoach = ({ session }) => {
  const navigate = useNavigate();
  const resultsRef = useRef(null);
  
  // App State
  const [jobDescription, setJobDescription] = useState('');
  const [interviewType, setInterviewType] = useState('Mixed'); // 'Technical', 'Behavioral', 'Mixed'
  const [activeStep, setActiveStep] = useState(1); // 1: Job, 2: Type, 3: Q&A, 4: Results
  
  // Loading and Error States
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadingStep, setLoadingStep] = useState('');
  
  // Q&A session states
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState(Array(10).fill(''));
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  
  // Results
  const [evaluation, setEvaluation] = useState(null);
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  
  // Premium & limits
  const [credits, setCredits] = useState(session ? 0 : 1);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [showSignupTeaser, setShowSignupTeaser] = useState(false);
  const [initialPlan, setInitialPlan] = useState(null);
  
  // Load prefilled job description
  useEffect(() => {
    const savedJob = sessionStorage.getItem('pending_job_desc');
    if (savedJob) setJobDescription(savedJob);
  }, []);

  // Sync credits & check for pending evaluation trigger from OAuth redirect
  useEffect(() => {
    const syncCreditsAndCheckPending = async () => {
      if (!session?.user) return;

      try {
        let { data, error: fetchError } = await supabase
          .from('profiles')
          .select('credits')
          .eq('id', session.user.id)
          .single();

        if (fetchError && fetchError.code === 'PGRST116') {
          const { data: newData, error: createError } = await supabase
            .from('profiles')
            .insert([{ id: session.user.id, email: session.user.email, credits: 3 }])
            .select()
            .single();
          
          if (createError) throw createError;
          data = newData;
        } else if (fetchError) {
          throw fetchError;
        }

        setCredits(data.credits);

        // Check if returning from redirect after finished interview
        const triggerEval = sessionStorage.getItem('pending_trigger_evaluation');
        const savedQuestions = sessionStorage.getItem('pending_interview_questions');
        const savedAnswers = sessionStorage.getItem('pending_interview_answers');
        const savedType = sessionStorage.getItem('pending_interview_type');
        const savedJob = sessionStorage.getItem('pending_interview_job_desc');

        if (triggerEval === 'true' && savedQuestions && savedAnswers && savedJob && savedType) {
          // Clear credentials
          sessionStorage.removeItem('pending_trigger_evaluation');
          sessionStorage.removeItem('pending_interview_questions');
          sessionStorage.removeItem('pending_interview_answers');
          sessionStorage.removeItem('pending_interview_type');
          sessionStorage.removeItem('pending_interview_job_desc');

          const parsedQuestions = JSON.parse(savedQuestions);
          const parsedAnswers = JSON.parse(savedAnswers);

          // Restore state
          setJobDescription(savedJob);
          setInterviewType(savedType);
          setQuestions(parsedQuestions);
          setAnswers(parsedAnswers);
          setActiveStep(3);
          setCurrentQuestionIndex(9); // show last question

          // Trigger evaluate API immediately
          setLoading(true);
          setError(null);
          setLoadingStep("Connecting to AI Interview coach...");

          const token = session.access_token;
          const res = await fetch('/api/evaluate-interview', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({
              jobDescription: savedJob,
              interviewType: savedType,
              questions: parsedQuestions,
              answers: parsedAnswers
            })
          });

          const json = await handleApiResponse(res, 'Evaluation failed');

          setEvaluation(json.data);
          if (json.credits !== undefined) setCredits(json.credits);
          setActiveStep(4);
        }
      } catch (err) {
        console.error("Error in sync credits / pending checker:", err);
        setError(err.message || "An error occurred while loading your profile.");
      } finally {
        setLoading(false);
      }
    };

    syncCreditsAndCheckPending();
  }, [session]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const handleStartPractice = async () => {
    if (!jobDescription.trim()) {
      setError("Please paste the job description first.");
      return;
    }
    setError(null);
    setLoading(true);
    setLoadingStep("Analyzing job requirements...");
    
    try {
      // Call unauthenticated API to generate 10 questions
      const res = await fetch('/api/generate-questions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ jobDescription, interviewType })
      });

      const json = await handleApiResponse(res, 'Failed to generate questions');

      setQuestions(json.data.questions);
      setAnswers(Array(10).fill(''));
      setCurrentQuestionIndex(0);
      setActiveStep(3);
    } catch (err) {
      console.error(err);
      setError(err.message || "An error occurred while generating interview questions.");
    } finally {
      setLoading(false);
    }
  };

  const getAuthToken = async () => {
    const { data } = await supabase.auth.getSession();
    return data?.session?.access_token;
  };

  const handleSubmitInterview = async () => {
    // If not logged in, trigger signup flow
    if (!session) {
      setLoading(true);
      setError(null);
      setLoadingStep("Processing final feedback...");
      
      // Simulate progress for 3 seconds, then show SignupTeaser
      setTimeout(() => {
        setLoadingStep("Synthesizing performance evaluation...");
        setTimeout(() => {
          setLoadingStep("Drafting suggested ideal answers...");
          setTimeout(() => {
            setLoading(false);
            
            // Save state to resume after signin
            sessionStorage.setItem('pending_interview_job_desc', jobDescription);
            sessionStorage.setItem('pending_interview_type', interviewType);
            sessionStorage.setItem('pending_interview_questions', JSON.stringify(questions));
            sessionStorage.setItem('pending_interview_answers', JSON.stringify(answers));
            sessionStorage.setItem('pending_trigger_evaluation', 'true');
            
            setShowSignupTeaser(true);
          }, 1000);
        }, 1000);
      }, 1000);
      return;
    }

    // If logged in, check credits
    if (Number(credits) <= 0) {
      setShowLimitModal(true);
      return;
    }

    setLoading(true);
    setError(null);
    setLoadingStep("Analyzing answer depth...");
    
    try {
      const token = await getAuthToken();
      const res = await fetch('/api/evaluate-interview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          jobDescription,
          interviewType,
          questions,
          answers
        })
      });

      const json = await handleApiResponse(res, 'Evaluation failed');

      setEvaluation(json.data);
      if (json.credits !== undefined) setCredits(json.credits);
      setActiveStep(4);
    } catch (err) {
      console.error(err);
      setError(err.message || "An error occurred while evaluating your interview answers.");
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = (newTotal) => {
    setCredits(newTotal);
    setShowLimitModal(false);
  };

  const handleServerVerifyPaypal = async (orderID) => {
    try {
      const token = await getAuthToken();
      const res = await fetch('/api/paypal-capture', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ orderID })
      });
      const json = await handleApiResponse(res, 'Payment verification failed');
      handlePaymentSuccess(json.credits);
    } catch (err) {
      console.error('PayPal verification error:', err);
      setError('Payment verification failed. Please contact support.');
    }
  };

  // Scroll to results when they load
  useEffect(() => {
    if (evaluation && resultsRef.current) {
      setTimeout(() => {
        resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [evaluation]);

  const getScoreColor = (score) => {
    if (score >= 70) return 'text-emerald-400 stroke-emerald-400 border-emerald-500/20';
    if (score >= 50) return 'text-yellow-400 stroke-yellow-400 border-yellow-500/20';
    return 'text-rose-400 stroke-rose-400 border-rose-500/20';
  };

  const getScoreBg = (score) => {
    if (score >= 70) return 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400';
    if (score >= 50) return 'bg-yellow-500/10 border border-yellow-500/20 text-yellow-400';
    return 'bg-rose-500/10 border border-rose-500/20 text-rose-400';
  };

  const currentStep = evaluation ? 4 : (questions.length > 0 ? 3 : (jobDescription ? 2 : 1));

  return (
    <div className="min-h-screen bg-[#0B0F19] text-gray-100 px-4 py-6 md:p-8 relative overflow-hidden">
      {/* Background Orbs */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute top-0 left-0 -translate-y-1/4 -translate-x-1/4 w-[40%] h-[40%] rounded-full bg-purple-600/10 blur-[120px]" />
        <div className="absolute bottom-0 right-0 translate-y-1/4 translate-x-1/4 w-[40%] h-[40%] rounded-full bg-indigo-600/10 blur-[120px]" />
      </div>

      {/* Paywall modal for 0 credits */}
      {showLimitModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-950/80 backdrop-blur-sm animate-in fade-in duration-200">
          <PaywallModal 
            onClose={() => { setShowLimitModal(false); setInitialPlan(null); }} 
            onPaymentSuccess={handlePaymentSuccess}
            onServerVerifyPaypal={handleServerVerifyPaypal}
            initialPlanId={initialPlan}
            userId={session?.user?.id}
            userEmail={session?.user?.email}
          />
        </div>
      )}

      {/* Signup Teaser Modal */}
      {showSignupTeaser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-950/90 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="max-w-md w-full bg-gray-900 border border-purple-500/30 px-6 py-8 rounded-3xl shadow-[0_0_50px_-10px_rgba(168,85,247,0.4)] text-center animate-in zoom-in-95 duration-300">
            <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-purple-500/20">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-2xl md:text-3xl font-extrabold text-white mb-3">🎯 Interview Complete!</h3>
            <p className="text-gray-300 text-sm md:text-base mb-6 leading-relaxed">
              We have graded all 10 responses, mapped them against the job description, and generated detailed feedback with ideal suggested answers.
            </p>
            <p className="text-purple-300/80 text-xs font-semibold mb-6">
              Create a free account to unlock your dashboard and score.
            </p>

            <div className="space-y-4">
              <button
                onClick={async () => {
                  try {
                    const { error } = await supabase.auth.signInWithOAuth({
                      provider: 'google',
                      options: {
                        redirectTo: `${window.location.origin}/login?redirect=/interview`
                      }
                    });
                    if (error) throw error;
                  } catch (err) {
                    setError(err.message);
                  }
                }}
                className="w-full px-4 py-3.5 bg-white hover:bg-gray-100 text-gray-900 font-extrabold rounded-xl transition-all flex items-center justify-center shadow-lg text-sm"
              >
                <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" className="w-5 h-5 mr-3" alt="Google" />
                Unlock Report with Google
              </button>

              <button
                onClick={() => {
                  navigate('/login?redirect=/interview');
                }}
                className="w-full px-4 py-3.5 bg-indigo-600 hover:bg-indigo-500 text-white font-extrabold rounded-xl transition-all shadow-[0_0_20px_-5px_rgba(99,102,241,0.5)] flex items-center justify-center text-sm"
              >
                Unlock Report with Email
                <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            </div>

            <p className="text-xs text-gray-500 mt-6 flex items-center justify-center">
              <CheckCircle2 className="w-3.5 h-3.5 mr-1 text-emerald-400" />
              1 Free session • No credit card required
            </p>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Navigation Bar */}
        <header className="mb-6">
          <div className="flex justify-between items-center py-4 mb-6 border-b border-white/5">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
              <div className="p-1.5 bg-purple-500/20 rounded-lg border border-purple-500/30">
                <Sparkles className="w-4 h-4 text-purple-400" />
              </div>
              <span className="text-base sm:text-lg font-bold tracking-tight text-white">CV Matcher<span className="text-purple-400">Pro</span></span>
            </div>
            
            <div className="flex items-center space-x-3">
              <button 
                onClick={() => navigate('/app')}
                className="text-xs sm:text-sm font-semibold text-indigo-400 hover:text-indigo-300 transition-colors mr-3"
              >
                ← Go to CV Matcher
              </button>
              
              {session ? (
                <>
                  <div className="hidden sm:flex items-center text-gray-400 bg-white/5 px-3 py-1.5 rounded-xl border border-white/10 text-xs font-medium">
                    <User className="w-3.5 h-3.5 mr-1.5 text-purple-400" />
                    <span className="max-w-[150px] truncate">{session?.user?.email}</span>
                  </div>
                  <button 
                    onClick={handleLogout}
                    className="flex items-center space-x-1.5 px-3.5 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-xl transition-all border border-gray-700 shadow-md text-xs font-medium"
                  >
                    <LogOut className="w-3.5 h-3.5" />
                    <span>Logout</span>
                  </button>
                </>
              ) : (
                <button 
                  onClick={() => navigate('/login?redirect=/interview')}
                  className="flex items-center space-x-1.5 px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold rounded-xl transition-all shadow-md text-xs md:text-sm"
                >
                  <User className="w-3.5 h-3.5" />
                  <span>Login / Register</span>
                </button>
              )}
            </div>
          </div>

          <div className="text-center px-4 my-8 md:my-10 animate-in fade-in duration-500">
            <h1 className="text-2xl sm:text-4xl md:text-5xl font-extrabold mb-3 tracking-tight text-white leading-tight">
              AI Interview <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-indigo-400 to-blue-400">Coach</span>
            </h1>
            <p className="text-gray-400 text-sm sm:text-base max-w-2xl mx-auto mb-4">
              Simulate high-pressure technical and behavioral interviews tailored to the specific job details. Get detailed feedback and scoring from expert AI.
            </p>
            <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-xs sm:text-sm font-medium">
              {session ? `Credits Remaining: ${credits}` : '1 Free Mock Interview Practice Available'}
            </div>
          </div>

          <StepIndicator currentStep={currentStep} />
        </header>

        {error && (
          <div className="mb-8 p-4 bg-rose-500/10 border border-rose-500/20 rounded-xl flex items-start text-rose-400 max-w-4xl mx-auto">
            <AlertCircle className="w-6 h-6 mr-3 shrink-0 mt-0.5" />
            <p>{error}</p>
          </div>
        )}

        {/* LOADING ANIMATION */}
        {loading && (
          <div className="w-full max-w-lg mt-6 bg-gray-900/50 border border-purple-500/20 rounded-2xl p-6 text-center animate-pulse mx-auto shadow-xl backdrop-blur-sm">
            <Loader2 className="w-10 h-10 text-purple-400 animate-spin mx-auto mb-4" />
            <h3 className="text-base font-semibold text-white mb-2">{loadingStep}</h3>
            <p className="text-xs text-gray-500 mb-4">This can take up to 30 seconds as the AI evaluates depth.</p>
            <div className="w-full bg-gray-950 rounded-full h-1.5 overflow-hidden">
              <div 
                className="bg-gradient-to-r from-purple-500 to-indigo-500 h-full rounded-full transition-all duration-700"
                style={{
                  width: loadingStep.includes("requirements") || loadingStep.includes("depth") ? "33%" : (loadingStep.includes("feedback") || loadingStep.includes("performance") ? "66%" : "95%")
                }}
              />
            </div>
          </div>
        )}

        {/* STEP 1: Paste Job Description */}
        {!loading && activeStep === 1 && (
          <div className="max-w-4xl mx-auto glass-panel p-6 sm:p-8 flex flex-col relative animate-in fade-in slide-in-from-bottom-4 duration-300">
            <div className="flex items-center mb-4 text-purple-400">
              <Briefcase className="w-6 h-6 mr-2" />
              <h2 className="text-xl font-bold text-white">Paste the Job Description</h2>
            </div>
            
            <textarea
              value={jobDescription}
              onChange={(e) => setJobDescription(e.target.value)}
              placeholder="Paste the LinkedIn or company job description here. The AI will formulate customized questions based on its explicit requirements..."
              className="w-full min-h-[300px] bg-gray-950/50 border border-gray-800 rounded-xl p-4 text-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all resize-none font-mono text-sm overflow-y-auto mb-6"
            />
            
            <div className="flex justify-end">
              <button
                onClick={() => {
                  if (!jobDescription.trim()) {
                    setError("Please paste the job description to proceed.");
                    return;
                  }
                  setError(null);
                  setActiveStep(2);
                }}
                className="group flex items-center space-x-2 px-6 py-3 bg-purple-600 hover:bg-purple-500 text-white rounded-xl font-bold text-base transition-all duration-300 shadow-lg shadow-purple-500/10"
              >
                <span>Choose Interview Type</span>
                <ChevronRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
              </button>
            </div>
          </div>
        )}

        {/* STEP 2: Choose Interview Type */}
        {!loading && activeStep === 2 && (
          <div className="max-w-4xl mx-auto glass-panel p-6 sm:p-8 relative animate-in fade-in slide-in-from-bottom-4 duration-300">
            <div className="flex items-center mb-6 text-purple-400">
              <BookOpen className="w-6 h-6 mr-2" />
              <h2 className="text-xl font-bold text-white">Choose Interview Type</h2>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {/* Technical */}
              <div 
                onClick={() => setInterviewType('Technical')}
                className={`p-6 rounded-2xl border transition-all cursor-pointer text-left flex flex-col relative group ${
                  interviewType === 'Technical' 
                    ? 'bg-purple-600/10 border-purple-500 shadow-[0_0_20px_-5px_rgba(168,85,247,0.3)]' 
                    : 'bg-gray-950 border-gray-800 hover:border-gray-700'
                }`}
              >
                <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Award className="w-5 h-5 text-purple-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Technical</h3>
                <p className="text-sm text-gray-400 leading-relaxed">
                  Focus on hard skills, framework concepts, design patterns, coding practices, or tools specified in the job description.
                </p>
                {interviewType === 'Technical' && (
                  <div className="absolute top-4 right-4 w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center text-white animate-in zoom-in-50">
                    <Check className="w-3.5 h-3.5" />
                  </div>
                )}
              </div>

              {/* Behavioral */}
              <div 
                onClick={() => setInterviewType('Behavioral')}
                className={`p-6 rounded-2xl border transition-all cursor-pointer text-left flex flex-col relative group ${
                  interviewType === 'Behavioral' 
                    ? 'bg-purple-600/10 border-purple-500 shadow-[0_0_20px_-5px_rgba(168,85,247,0.3)]' 
                    : 'bg-gray-950 border-gray-800 hover:border-gray-700'
                }`}
              >
                <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <TrendingUp className="w-5 h-5 text-purple-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Behavioral</h3>
                <p className="text-sm text-gray-400 leading-relaxed">
                  Focus on interpersonal skills, situational decision-making, conflict resolution, leadership, and teamwork (STAR method).
                </p>
                {interviewType === 'Behavioral' && (
                  <div className="absolute top-4 right-4 w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center text-white animate-in zoom-in-50">
                    <Check className="w-3.5 h-3.5" />
                  </div>
                )}
              </div>

              {/* Mixed */}
              <div 
                onClick={() => setInterviewType('Mixed')}
                className={`p-6 rounded-2xl border transition-all cursor-pointer text-left flex flex-col relative group ${
                  interviewType === 'Mixed' 
                    ? 'bg-purple-600/10 border-purple-500 shadow-[0_0_20px_-5px_rgba(168,85,247,0.3)]' 
                    : 'bg-gray-950 border-gray-800 hover:border-gray-700'
                }`}
              >
                <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Sparkles className="w-5 h-5 text-purple-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Mixed</h3>
                <p className="text-sm text-gray-400 leading-relaxed">
                  A perfect 50/50 balance, testing both your specific hardware/software domain competencies and soft situational traits.
                </p>
                {interviewType === 'Mixed' && (
                  <div className="absolute top-4 right-4 w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center text-white animate-in zoom-in-50">
                    <Check className="w-3.5 h-3.5" />
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-between items-center">
              <button
                onClick={() => setActiveStep(1)}
                className="text-gray-400 hover:text-white transition-colors text-sm font-semibold"
              >
                ← Back to Job Description
              </button>

              <button
                onClick={handleStartPractice}
                className="group relative px-8 py-3.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-bold rounded-xl transition-all duration-300 shadow-lg shadow-purple-500/10 flex items-center overflow-hidden"
              >
                <Sparkles className="w-5 h-5 mr-2 animate-pulse text-white" />
                <span>Start Practice Session</span>
              </button>
            </div>
          </div>
        )}

        {/* STEP 3: INTERACTIVE Q&A */}
        {!loading && activeStep === 3 && questions.length > 0 && (
          <div className="max-w-4xl mx-auto glass-panel p-6 sm:p-8 flex flex-col relative animate-in fade-in slide-in-from-bottom-4 duration-300">
            {/* Top Indicator */}
            <div className="flex justify-between items-center mb-6">
              <div className="text-xs uppercase tracking-widest text-purple-400 font-bold">
                Question {currentQuestionIndex + 1} of 10
              </div>
              <div className="w-32 bg-gray-950 rounded-full h-2 overflow-hidden">
                <div 
                  className="bg-purple-500 h-full rounded-full transition-all duration-300"
                  style={{ width: `${((currentQuestionIndex + 1) / 10) * 100}%` }}
                />
              </div>
            </div>

            {/* Question Text */}
            <div className="mb-6 p-5 sm:p-6 bg-purple-950/20 border border-purple-500/10 rounded-2xl">
              <h3 className="text-lg sm:text-xl font-semibold text-white leading-relaxed">
                {questions[currentQuestionIndex]}
              </h3>
            </div>

            {/* Answer Input */}
            <div className="flex flex-col mb-8">
              <label className="block text-sm font-medium text-gray-300 mb-2">Your Answer</label>
              <textarea
                value={answers[currentQuestionIndex]}
                onChange={(e) => {
                  const updated = [...answers];
                  updated[currentQuestionIndex] = e.target.value;
                  setAnswers(updated);
                }}
                placeholder="Type your response. Take your time and be specific. Mention concrete examples where applicable..."
                className="w-full min-h-[180px] bg-gray-950/50 border border-gray-800 rounded-xl p-4 text-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all resize-none font-mono text-sm overflow-y-auto"
              />
              <div className="text-right text-xs text-gray-500 mt-2">
                Characters: {answers[currentQuestionIndex]?.length || 0}
              </div>
            </div>

            {/* Navigation buttons */}
            <div className="flex justify-between items-center border-t border-white/5 pt-6">
              <button
                onClick={() => {
                  if (currentQuestionIndex > 0) {
                    setCurrentQuestionIndex(currentQuestionIndex - 1);
                  }
                }}
                disabled={currentQuestionIndex === 0}
                className="px-4 py-2 text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-sm font-semibold"
              >
                ← Previous Question
              </button>

              {currentQuestionIndex < 9 ? (
                <button
                  onClick={() => setCurrentQuestionIndex(currentQuestionIndex + 1)}
                  className="px-6 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl text-sm font-bold transition-all"
                >
                  Next Question →
                </button>
              ) : (
                <button
                  onClick={handleSubmitInterview}
                  className="px-8 py-3.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white rounded-xl font-bold text-base transition-all duration-300 shadow-lg shadow-purple-500/20 flex items-center"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  <span>
                    {session ? "Submit Practice (1 Credit)" : "Submit Practice"}
                  </span>
                </button>
              )}
            </div>
          </div>
        )}

        {/* STEP 4: RESULTS DASHBOARD */}
        {!loading && activeStep === 4 && evaluation && (
          <div ref={resultsRef} className="space-y-8 animate-in fade-in slide-in-from-bottom-8 duration-700 max-w-5xl mx-auto">
            
            {/* Top Stat Summary Grid */}
            <div className="grid md:grid-cols-3 gap-6">
              <div className="glass-panel p-8 flex flex-col items-center justify-center text-center">
                <div className="relative w-40 h-40 mb-4 flex items-center justify-center">
                  <svg className="w-full h-full transform -rotate-90 absolute inset-0" viewBox="0 0 36 36">
                    <path className="text-gray-800 stroke-current" strokeWidth="3" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                    <path className={`${getScoreColor(evaluation.score)} transition-all duration-1000 ease-out`} strokeWidth="3" strokeDasharray={`${evaluation.score}, 100`} fill="none" strokeLinecap="round" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className={`text-5xl font-bold ${getScoreColor(evaluation.score)}`}>{evaluation.score}</span>
                    <span className="text-gray-400 text-sm mt-1">/ 100</span>
                  </div>
                </div>
                <div className={`px-4 py-1.5 rounded-full font-medium ${getScoreBg(evaluation.score)}`}>
                  {evaluation.score >= 80 ? "Hire Candidate" : (evaluation.score >= 60 ? "Strong Contender" : "Needs Practice")}
                </div>
              </div>

              <div className="glass-panel p-6 md:col-span-2 flex flex-col justify-center">
                <h3 className="text-xl font-bold mb-4 flex items-center text-purple-400">
                  <TrendingUp className="w-5 h-5 mr-2" /> Performance Overview
                </h3>
                <p className="text-gray-300 leading-relaxed text-base">
                  You simulated a <strong className="text-white">{interviewType}</strong> interview. The AI evaluated your answers based on industry benchmarks and the job requirements you pasted. Review your key strengths and improve on the recommendations below to perfect your skills.
                </p>
              </div>
            </div>

            {/* Strengths & Weaknesses */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="glass-panel p-6 border-emerald-500/20 bg-emerald-500/5">
                <h3 className="text-lg font-bold mb-6 flex items-center text-emerald-400">
                  <CheckCircle2 className="w-5 h-5 mr-2" /> Key Strengths
                </h3>
                <ul className="space-y-4">
                  {evaluation.strengths?.map((strength, i) => (
                    <li key={i} className="flex items-start">
                      <div className="mt-1.5 mr-3 shrink-0 w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                      <span className="text-gray-300 text-sm leading-relaxed">{strength}</span>
                    </li>
                  ))}
                  {(!evaluation.strengths || evaluation.strengths.length === 0) && (
                    <li className="text-gray-500 text-sm italic">No specific strengths listed</li>
                  )}
                </ul>
              </div>

              <div className="glass-panel p-6 border-indigo-500/20 bg-indigo-500/5">
                <h3 className="text-lg font-bold mb-6 flex items-center text-indigo-400">
                  <ArrowRight className="w-5 h-5 mr-2" /> Areas for Improvement
                </h3>
                <ul className="space-y-4">
                  {evaluation.improvements?.map((imp, i) => (
                    <li key={i} className="flex items-start">
                      <div className="mt-1.5 mr-3 shrink-0 w-2 h-2 rounded-full bg-indigo-400" />
                      <span className="text-gray-300 text-sm leading-relaxed">{imp}</span>
                    </li>
                  ))}
                  {(!evaluation.improvements || evaluation.improvements.length === 0) && (
                    <li className="text-gray-500 text-sm italic">No clear improvements highlighted</li>
                  )}
                </ul>
              </div>
            </div>

            {/* Q&A Breakdown */}
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                <BookOpen className="w-5 h-5 mr-2 text-purple-400" /> Question-by-Question Review
              </h3>

              {evaluation.evaluations?.map((item, index) => {
                const isExpanded = expandedQuestion === index;
                return (
                  <div 
                    key={index}
                    className={`glass-panel border transition-all ${
                      isExpanded ? 'border-purple-500/30' : 'hover:border-gray-800 border-gray-900 bg-gray-950/40'
                    }`}
                  >
                    {/* Header bar clickable to expand */}
                    <div 
                      onClick={() => setExpandedQuestion(isExpanded ? null : index)}
                      className="p-5 flex items-center justify-between cursor-pointer select-none"
                    >
                      <div className="flex items-start space-x-3 pr-4">
                        <span className="text-sm font-bold text-purple-400 shrink-0 mt-0.5">Q{index + 1}.</span>
                        <h4 className="font-semibold text-gray-200 text-sm sm:text-base line-clamp-1 text-left">
                          {item.question}
                        </h4>
                      </div>
                      
                      <div className="flex items-center space-x-3 shrink-0">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${getScoreBg(item.score)}`}>
                          {item.score} / 100
                        </span>
                        {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                      </div>
                    </div>

                    {/* Expandable content */}
                    {isExpanded && (
                      <div className="px-5 pb-6 pt-2 border-t border-white/5 space-y-6 animate-in fade-in duration-200">
                        {/* Question Text in full */}
                        <div className="p-3.5 bg-gray-900/50 rounded-xl text-gray-400 text-xs font-mono">
                          <strong className="text-purple-400 block mb-1">Question:</strong>
                          {item.question}
                        </div>

                        {/* Candidate Answer */}
                        <div>
                          <h5 className="text-xs uppercase tracking-wider font-bold text-gray-500 mb-2">Your Answer:</h5>
                          <div className="p-4 bg-gray-950 rounded-xl border border-gray-800 text-gray-300 text-sm leading-relaxed whitespace-pre-wrap font-sans">
                            {item.answer || <span className="italic text-gray-600">No answer provided</span>}
                          </div>
                        </div>

                        {/* AI Feedback */}
                        <div>
                          <h5 className="text-xs uppercase tracking-wider font-bold text-gray-500 mb-2 flex items-center text-purple-400">
                            <TrendingUp className="w-4 h-4 mr-1.5" /> AI Analysis & Suggestions:
                          </h5>
                          <div className="p-4 bg-purple-950/10 border border-purple-500/10 rounded-xl text-gray-300 text-sm leading-relaxed">
                            {item.feedback}
                          </div>
                        </div>

                        {/* Perfect Answer Suggestion */}
                        <div>
                          <div className="flex justify-between items-center mb-2">
                            <h5 className="text-xs uppercase tracking-wider font-bold text-gray-500 flex items-center text-indigo-400">
                              <Sparkles className="w-4 h-4 mr-1.5 animate-pulse" /> Suggested Ideal Answer:
                            </h5>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(item.perfect_answer);
                              }}
                              className="flex items-center space-x-1 px-2.5 py-1 bg-indigo-600/10 hover:bg-indigo-600/20 text-indigo-300 border border-indigo-500/20 rounded-lg text-xs font-semibold transition-all"
                            >
                              <Copy className="w-3 h-3" />
                              <span>Copy Suggested Answer</span>
                            </button>
                          </div>
                          <div className="p-4 bg-indigo-950/10 border border-indigo-500/15 rounded-xl text-gray-200 text-sm leading-relaxed italic font-serif">
                            "{item.perfect_answer}"
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Restart card */}
            <div className="glass-panel p-6 border-purple-500/20 bg-purple-500/5 text-center mt-12">
              <h4 className="text-lg font-bold text-white mb-2">Practice again?</h4>
              <p className="text-gray-400 text-sm mb-4">You can load a new job description or retry a different interview style.</p>
              <button
                onClick={() => {
                  setActiveStep(1);
                  setEvaluation(null);
                  setQuestions([]);
                  setAnswers(Array(10).fill(''));
                }}
                className="px-6 py-2.5 bg-purple-600 hover:bg-purple-500 text-white font-bold rounded-xl text-sm transition-all"
              >
                Start New Practice Session
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InterviewCoach;
